import { createObjectSlug, reFileExtension } from '@glass/common/modules/utils/objectSlug';
import { currentOrigin } from '@glass/env/modules/origins/constants';
import { QA_PREFIX, SEO_TITLE_PREFIX_ROCKET } from '@glass/web/modules/images/constants';
import { VERCEL_PROTECTION_BYPASS_QUERY } from '@glass/web/modules/pages/queryParams';

const CLOUDFLARE_IMAGE_PREFIX = '/cdn-cgi/image';
const CLOUDFLARE_SETTING_DELIMITER = ',';

export const WIDTH_KEY = 'w';
export const HEIGHT_KEY = 'h';
export const FIT_KEY = 'fit';
export const QUALITY_KEY = 'q';

const defaultCloudflareOptions = {
  f: 'auto',
};

export interface GenerateImageSrcParams {
  seoTitle?: string;
  seoTitlePrefix?: string;
  path?: string | null;
  width?: number;
  height?: number;
  external?: boolean;
  fit?: string;
  quality?: number;
  origin?: string;
  bypassSecret?: string;
}

const generateImageSrc = ({
  seoTitle,
  seoTitlePrefix = SEO_TITLE_PREFIX_ROCKET,
  path,
  width,
  height,
  external,
  fit,
  quality = 80,
  origin = process.env.NEXT_PUBLIC_IMAGE_ORIGIN || currentOrigin,
  bypassSecret = process.env.NEXT_PUBLIC_VERCEL_AUTOMATION_BYPASS_SECRET,
}: GenerateImageSrcParams): string => {
  if (!path) {
    return '';
  }

  let finalPath = path[0] === '/' ? path : `/${path}`;
  const finalSearchParams = new URLSearchParams();

  if (!external) {
    if (origin?.includes?.('://qa.')) {
      finalPath = `/${QA_PREFIX}${finalPath}`;
    }
    // rocket resume cloudflare worker
    if (seoTitle) {
      const newEnding = finalPath.replace(reFileExtension, `/${seoTitle}.$1`);
      finalPath = `${seoTitlePrefix}${newEnding}`;
    }

    if (bypassSecret) {
      finalSearchParams.set(VERCEL_PROTECTION_BYPASS_QUERY, bypassSecret);
    }
  }

  let cfFit: string | undefined;

  if (height) {
    cfFit = 'cover';
  } else if (width) {
    cfFit = 'scale-down';
  }

  const responsiveImageSlug = createObjectSlug(
    {
      ...defaultCloudflareOptions,
      [FIT_KEY]: fit || cfFit,
      [HEIGHT_KEY]: height,
      [WIDTH_KEY]: width,
      [QUALITY_KEY]: quality,
    },
    { settingsDelimiter: CLOUDFLARE_SETTING_DELIMITER },
  );

  finalPath = `${CLOUDFLARE_IMAGE_PREFIX}/${responsiveImageSlug}${finalPath}`;

  const basePath = `${origin || ''}${finalPath}`;

  if (finalSearchParams.size > 0) {
    return `${basePath}?${finalSearchParams.toString()}`;
  }

  return basePath;
};

export default generateImageSrc;
